import React, {useMemo, useState} from 'react';
import {Download, Eye, X, ChevronRight, ChevronLeft, Plus, Minus, RotateCcw, RotateCw, Undo2, History, Eraser} from 'lucide-react';
import { toastr } from "react-redux-toastr";
import {getFile} from "../utils/network";
import {sanitizeHtml} from "../utils/sanitizeHtml";
import ReactDOM from "react-dom";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import {I18n} from "react-i18next";
import _ from 'lodash'

const ImageViewer = ({ src, filename }) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    const resetView = () => {
        setZoom(1);
        setRotation(0);
    };

    const zoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
    const zoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25));
    const rotateLeft = () => setRotation(prev => prev - 90);
    const rotateRight = () => setRotation(prev => prev + 90);

    return (
        <div style={{
            position: 'relative',
            height: '80vh',
            overflow: 'hidden',
            backgroundColor: '#282828'//'#f5f5f5'
        }}>
            {/* Image Controls */}
            <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 10,
                display: 'flex',
                gap: '5px',
                background: '#3C3C3C',//'rgba(0,0,0,0.8)',
                padding: '8px',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
                <button
                    onClick={rotateLeft}
                    style={{
                        background: '#444',
                        border: 'none',
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                    title="Rotate Left"
                >
                    <RotateCcw size={14}/>
                </button>
                <button
                    onClick={rotateRight}
                    style={{
                        background: '#444',
                        border: 'none',
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                    title="Rotate Right"
                >
                    <RotateCw size={14}/>
                </button>
                <div style={{ width: '1px', background: '#666', margin: '4px 0' }}></div>
                <button
                    onClick={zoomOut}
                    disabled={zoom <= 0.25}
                    style={{
                        background: zoom <= 0.25 ? '#666' : '#444',
                        border: 'none',
                        color: 'white',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        cursor: zoom <= 0.25 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                    title="Zoom Out"
                >
                    <Minus size={14}/>
                </button>
                <span style={{
                    color: 'white',
                    padding: '6px 8px',
                    fontSize: '12px',
                    minWidth: '50px',
                    textAlign: 'center',
                    fontWeight: '500'
                }}>
                    {Math.round(zoom * 100)}%
                </span>
                <button
                    onClick={zoomIn}
                    disabled={zoom >= 3}
                    style={{
                        background: zoom >= 3 ? '#666' : '#444',
                        border: 'none',
                        color: 'white',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        cursor: zoom >= 3 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                    title="Zoom In"
                >
                    <Plus size={14}/>
                </button>
                <div style={{ width: '1px', background: '#666', margin: '4px 0' }}></div>
                <button
                    onClick={resetView}
                    style={{
                        background: '#444',
                        border: 'none',
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                    title="Reset View"
                >
                    <Eraser size={14}/>
                </button>
            </div>

            {/* Image Container */}
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto'
            }}>
                <img
                    src={src}
                    alt={filename}
                    style={{
                        transform: `scale(${zoom}) rotate(${rotation}deg)`,
                        transition: 'transform 0.2s ease-out',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        //aspectRatio: '4/3',
                        //cursor: zoom > 1 ? 'grab' : 'default',
                    }}
                    onLoad={resetView}
                    draggable={false}
                />
            </div>
        </div>
    );
};


const docxFilesStyle = `
    .docx-container h1, .docx-container h2, .docx-container h3,
    .docx-container h4, .docx-container h5, .docx-container h6 {
        margin-top: 1em;
    }
    .docx-container p {
        margin-bottom: 1em;
    }
    .docx-container table {
        border-collapse: collapse;
        margin: 1em 0;
        width: 100%;
    }
    .docx-container td, .docx-container th {
        border: 1px solid black;
        padding: 8px;
    }
    .docx-container img, .docx-container .docx-image {
        max-width: 100%;
        height: auto !important;
        max-height: 600px;
    }
    .docx-container blockquote {
        margin: 1em 0;
        padding: 10px 20px;
        border-left: 5px solid #eee;
        background-color: #f9f9f9;
    }
    .docx-container blockquote.intense {
        border-left-color: #009879;
        font-style: italic;
    }
    .docx-container h1 {
        font-size: 2.5em;
        margin-bottom: 0.5em;
    }
    .docx-container h2 {
        font-size: 1.8em;
        margin-top: 0;
        margin-bottom: 1.5em;
    }
    .docx-container li.bullet {
        list-style-type: disc;
        margin-left: 2em;
    }
    .docx-container li.number {
        list-style-type: decimal;
        margin-left: 2em;
    }
    .docx-container .toc-heading {
        font-size: 1.5em;
        margin-top: 2em;
    }
    .docx-container .toc-1 {
        margin-left: 0;
    }
    .docx-container .toc-2 {
        margin-left: 1.5em;
    }
    .docx-container .toc-3 {
        margin-left: 3em;
    }
`

// ─── Inline EML Parser ───────────────────────────────────────────────────────

const decodeEncodedWord = (str) => {
    if (!str) return str;
    // Remove whitespace between consecutive encoded words (RFC 2047 — whitespace is not content)
    const normalized = str.replace(/\?=\s+=\?/g, '?==?');
    return normalized.replace(/=\?([^?]+)\?(B|Q)\?([^?]*)\?=/gi, (_, charset, encoding, text) => {
        try {
            if (encoding.toUpperCase() === 'B') {
                const binary = atob(text);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                return new TextDecoder(charset).decode(bytes);
            } else {
                // collect raw bytes first, then decode with charset (handles multibyte UTF-8)
                const src = text.replace(/_/g, ' ');
                const bytes = [];
                let i = 0;
                while (i < src.length) {
                    if (src[i] === '=' && i + 2 < src.length) {
                        bytes.push(parseInt(src.substring(i + 1, i + 3), 16));
                        i += 3;
                    } else {
                        bytes.push(src.charCodeAt(i));
                        i++;
                    }
                }
                return new TextDecoder(charset).decode(new Uint8Array(bytes));
            }
        } catch (e) { return str; }
    });
};

const decodeQuotedPrintable = (str, charset) => {
    const raw = str.replace(/=\r?\n/g, '').replace(/=([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    try {
        const bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
        return new TextDecoder(charset || 'utf-8').decode(bytes);
    } catch (e) { return raw; }
};

const decodeBase64Body = (str, charset) => {
    try {
        const binary = atob(str.replace(/\s/g, ''));
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new TextDecoder(charset || 'utf-8').decode(bytes);
    } catch (e) { return str; }
};

const parseHeaders = (headerText) => {
    const unfolded = headerText.replace(/\r?\n[\t ]+/g, ' ');
    const headers = {};
    unfolded.split(/\r?\n/).forEach(line => {
        const colonIdx = line.indexOf(':');
        if (colonIdx > 0) {
            const key = line.substring(0, colonIdx).trim().toLowerCase();
            const value = line.substring(colonIdx + 1).trim();
            headers[key] = value;
        }
    });
    return headers;
};

const getCharset = (contentType) => {
    const match = contentType.match(/charset=["']?([^"';\s]+)["']?/i);
    return match ? match[1] : 'utf-8';
};

const getBoundary = (contentType) => {
    const match = contentType.match(/boundary=["']?([^"';\r\n]+)["']?/i);
    return match ? match[1].trim() : null;
};

const decodePartBody = (body, headers) => {
    const encoding = (headers['content-transfer-encoding'] || '').toLowerCase().trim();
    const charset = getCharset(headers['content-type'] || '');
    if (encoding === 'base64') return decodeBase64Body(body, charset);
    if (encoding === 'quoted-printable') return decodeQuotedPrintable(body, charset);
    return body;
};

const parseMIMEParts = (text, contentType) => {
    const result = { html: null, text: null, attachments: [] };
    const boundary = getBoundary(contentType);
    if (!boundary) return result;

    const escaped = boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp('--' + escaped + '(?:--)?\\r?\\n?'));

    parts.slice(1).forEach(part => {
        if (!part || part.trim() === '' || part.trim() === '--') return;
        const splitIdx = part.search(/\r?\n\r?\n/);
        if (splitIdx === -1) return;

        const partHeaders = parseHeaders(part.substring(0, splitIdx));
        const partBody = part.substring(splitIdx).replace(/^\r?\n/, '');
        const partContentType = partHeaders['content-type'] || '';
        const partDisposition = partHeaders['content-disposition'] || '';
        const contentId = (partHeaders['content-id'] || '').replace(/[<>]/g, '').trim();

        if (partContentType.includes('multipart/')) {
            const sub = parseMIMEParts(partBody, partContentType);
            if (sub.html && !result.html) result.html = sub.html;
            if (sub.text && !result.text) result.text = sub.text;
            result.attachments.push(...sub.attachments);
        } else if (partDisposition.includes('attachment') || (/filename\*?=/i.test(partDisposition) || /name\*?=/i.test(partContentType)) || contentId) {
            const filenameMatch = partDisposition.match(/filename\*?=["']?(?:[^']*'')?([^"';\r\n]+)["']?/i)
                || partContentType.match(/name\*?=["']?(?:[^']*'')?([^"';\r\n]+)["']?/i);
            const filename = filenameMatch
                ? decodeEncodedWord(decodeURIComponent(filenameMatch[1].trim()))
                : 'attachment';
            const encoding = (partHeaders['content-transfer-encoding'] || '').toLowerCase().trim();
            let content;
            if (encoding === 'base64') {
                const binary = atob(partBody.replace(/\s/g, ''));
                content = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) content[i] = binary.charCodeAt(i);
            } else {
                content = new TextEncoder().encode(partBody);
            }
            const mimeType = partContentType.split(';')[0].trim();
            const isInline = !!contentId && !partDisposition.includes('attachment');
            result.attachments.push({ filename, content, mimeType, contentId, isInline });
        } else if (partContentType.includes('text/html')) {
            const decoded = decodePartBody(partBody, partHeaders);
            if (decoded.trim()) {
                if (!result.html) {
                    result.html = decoded;
                } else {
                    // extract body content from subsequent HTML parts and append
                    const bodyMatch = decoded.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                    const fragment = bodyMatch ? bodyMatch[1] : decoded;
                    if (fragment.trim()) {
                        const existingBodyMatch = result.html.match(/(<body[^>]*>)([\s\S]*)(<\/body>)/i);
                        if (existingBodyMatch) {
                            result.html = result.html.replace(existingBodyMatch[0], existingBodyMatch[1] + existingBodyMatch[2] + fragment + existingBodyMatch[3]);
                        } else {
                            result.html += fragment;
                        }
                    }
                }
            }
        } else if (partContentType.includes('text/plain')) {
            const decoded = decodePartBody(partBody, partHeaders);
            if (decoded.trim()) {
                result.text = result.text ? result.text + '\n' + decoded : decoded;
            }
        }
    });

    return result;
};

const parseEML = async (blob) => {
    const text = await blob.text();
    const splitIdx = text.search(/\r?\n\r?\n/);
    if (splitIdx === -1) return { subject: '', from: null, to: [], cc: [], date: '', html: null, text: null, attachments: [] };

    const headers = parseHeaders(text.substring(0, splitIdx));
    const body = text.substring(splitIdx).replace(/^\r?\n/, '');
    const contentType = headers['content-type'] || 'text/plain';

    const parseAddress = (str) => {
        if (!str) return null;
        const decoded = decodeEncodedWord(str);
        const match = decoded.match(/^(.*?)\s*<([^>]+)>$/);
        if (match) return { name: match[1].trim().replace(/^"|"$/g, ''), address: match[2] };
        return { name: '', address: decoded.trim() };
    };

    const parseAddressList = (str) => {
        if (!str) return [];
        return str.split(',').map(s => parseAddress(s.trim())).filter(Boolean);
    };

    let html = null, plainText = null, attachments = [];

    if (contentType.includes('multipart/')) {
        const parsed = parseMIMEParts(body, contentType);
        html = parsed.html;
        plainText = parsed.text;
        attachments = parsed.attachments;
    } else {
        const decoded = decodePartBody(body, headers);
        if (contentType.includes('text/html')) html = decoded;
        else plainText = decoded;
    }

    // Replace cid: references in HTML with inline blob URLs
    if (html && attachments.length > 0) {
        attachments.forEach(att => {
            if (att.contentId) {
                const blobUrl = URL.createObjectURL(new Blob([att.content], { type: att.mimeType }));
                html = html.replace(new RegExp('cid:' + att.contentId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), blobUrl);
            }
        });
    }

    return {
        subject: decodeEncodedWord(headers['subject'] || ''),
        from: parseAddress(headers['from']),
        to: parseAddressList(headers['to']),
        cc: parseAddressList(headers['cc']),
        date: headers['date'] || '',
        html,
        text: plainText,
        attachments: attachments.filter(a => !a.isInline),
    };
};

// ─────────────────────────────────────────────────────────────────────────────

const getFileType = (fileName= '') => {
    const extension = fileName.split('.').pop().toLowerCase();
    const imageTypes = ['png', 'jpg', 'jpeg'];
    const videosTypes = ['mp4', 'm4v', 'mov', 'qt']
    const pdfTypes = ['pdf'];
    const textTypes = ['txt', 'xml'];
    const csvType = ['csv'];
    const xlsxType = ['xlsx'];
    const docxTypes = ['docx'];
    const emlTypes = ['eml'];

    if (imageTypes.includes(extension)) return 'image';
    if (emlTypes.includes(extension)) return 'eml';
    if (videosTypes.includes(extension)) return 'video';
    if (pdfTypes.includes(extension)) return 'pdf';
    if (textTypes.includes(extension)) return 'text';
    if (csvType.includes(extension)) return 'csv';
    if (xlsxType.includes(extension)) return 'xlsx';
    if (docxTypes.includes(extension)) return 'docx';

    return 'unknown';
};

const parseXLSX = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });

    const sheetsData = {};

    workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const merges = worksheet['!merges'] || [];
        const cols = worksheet['!cols'] || [];

        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const numCols = range.e.c - range.s.c + 1;

        const data = [];
        const styles = [];

        for (let r = range.s.r; r <= range.e.r; r++) {
            const rowData = new Array(numCols).fill('');
            const rowStyles = new Array(numCols).fill(null);
            for (let c = range.s.c; c <= range.e.c; c++) {
                const cellAddress = XLSX.utils.encode_cell({ r, c });
                const cell = worksheet[cellAddress];
                if (cell) {
                    rowData[c - range.s.c] = cell.w !== undefined ? cell.w : (cell.v !== undefined ? cell.v : '');
                    if (cell.s) {
                        const cellStyle = {};
                        const bgArgb = cell.s.fgColor?.rgb || cell.s.fill?.fgColor?.rgb;
                        if (bgArgb && bgArgb.length >= 6) {
                            const rgb = bgArgb.slice(-6);
                            if (rgb !== 'FFFFFF' && rgb !== 'ffffff') cellStyle.backgroundColor = `#${rgb}`;
                        }
                        if (cell.s.font?.bold) cellStyle.fontWeight = 'bold';
                        const fontArgb = cell.s.font?.color?.rgb;
                        if (fontArgb && fontArgb.length >= 6) cellStyle.color = `#${fontArgb.slice(-6)}`;
                        if (Object.keys(cellStyle).length > 0) rowStyles[c - range.s.c] = cellStyle;
                    }
                }
            }
            data.push(rowData);
            styles.push(rowStyles);
        }

        sheetsData[sheetName] = { data, styles, merges, cols };
    });

    return sheetsData;
};



const parseCSV = (csvText) => {
    const results = Papa.parse(csvText.trim(), {skipEmptyLines: true});
    return results.data; // 2D array
};

const processBlob = async (blob, fileType) => {
    try {
        if (fileType === 'csv') {
            const text = await blob.text();
            return { previewData: parseCSV(text), previewUrl: null };
        } else if (fileType === 'xlsx') {
            const data = await parseXLSX(blob);
            return { previewData: data, previewUrl: null };
        } else if (fileType === 'text') {
            const text = await blob.text();
            return { previewData: text, previewUrl: null };
        } else if (fileType === 'docx') {
            const arrayBuffer = await blob.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            return { previewData: result.value, previewUrl: null };
        } else if (fileType === 'eml') {
            const email = await parseEML(blob);
            return { previewData: email, previewUrl: null };
        } else {
            const url = URL.createObjectURL(blob);
            return { previewUrl: url, previewData: null };
        }
    } catch (error) {
        if (error.message.includes('password-protected')) {
            return { error: { type: 'protectedFileError', message: 'FILE_PROTECTED_ERROR' } };
        } else if (error.message.includes('corrupt') || error.message.includes("Can't find end of central directory") || error.message.includes("is this a zip file")) {
            return { error: { type: 'corruptedFileError', message: 'FILE_CORRUPT_ERROR' } };
        } else {
            return { error: { type: 'generalError', message: 'FILE_GENERAL_ERROR' } };
        }
    }
};

const renderCSVTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) return null;

    return (
        <div style={{ overflowX: 'auto', maxHeight: '80vh' }}>
            <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                margin: '1rem 0',
                boxShadow: '0 0 20px rgba(0, 0, 0, 0.15)'
            }}>
                <thead>
                <tr style={{
                    backgroundColor: '#009879',
                    color: '#ffffff',
                    textAlign: 'left'
                }}>
                    {data[0].map((header, index) => (
                        <th key={index} style={{
                            padding: '12px 15px',
                            borderRight: '1px solid #dddddd'
                        }}>
                            {header}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {data.slice(1).map((row, rowIndex) => (
                    <tr key={rowIndex} style={{
                        borderBottom: '1px solid #dddddd',
                        ':nth-of-type(even)': { backgroundColor: '#f3f3f3' }
                    }}>
                        {row.map((cell, cellIndex) => (
                            <td key={cellIndex} style={{
                                padding: '12px 15px',
                                borderRight: '1px solid #dddddd'
                            }}>
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const renderXLSXTable = (data, selectedSheet, setSelectedSheet) => {
    if (!data || Object.keys(data).length === 0) return null;

    const sheetNames = Object.keys(data);
    const activeSheet = selectedSheet || sheetNames[0];
    const { data: sheetData, styles, merges, cols } = data[activeSheet];

    const mergedMap = {};
    merges?.forEach(({ s, e }) => {
        mergedMap[`${s.r}-${s.c}`] = {
            rowSpan: e.r - s.r + 1,
            colSpan: e.c - s.c + 1
        };
        // masquer les cellules fusionnées (autres que le coin haut-gauche)
        for (let r = s.r; r <= e.r; r++) {
            for (let c = s.c; c <= e.c; c++) {
                if (r !== s.r || c !== s.c) {
                    mergedMap[`${r}-${c}`] = 'skip';
                }
            }
        }
    });

    return (
        <div>
            <div style={{ overflowX: 'auto', maxHeight: '80vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                    {sheetData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => {
                                const key = `${rowIndex}-${cellIndex}`;
                                const mergeInfo = mergedMap[key];
                                if (mergeInfo === 'skip') return null;

                                const cellProps = {};
                                if (mergeInfo) {
                                    if (mergeInfo.rowSpan > 1) cellProps.rowSpan = mergeInfo.rowSpan;
                                    if (mergeInfo.colSpan > 1) cellProps.colSpan = mergeInfo.colSpan;
                                }

                                return (
                                    <td
                                        key={cellIndex}
                                        {...cellProps}
                                        style={{
                                            padding: '12px 15px',
                                            border: '1px solid #dddddd',
                                            minWidth: `${(cols[cellIndex]?.wch || 10) * 14}px`,
                                            ...(styles?.[rowIndex]?.[cellIndex] || {}),
                                            backgroundColor: styles?.[rowIndex]?.[cellIndex]?.backgroundColor
                                                || (rowIndex === 0 ? '#009879' : 'inherit'),
                                            color: styles?.[rowIndex]?.[cellIndex]?.color
                                                || (rowIndex === 0 ? '#fff' : '#000'),
                                            fontWeight: styles?.[rowIndex]?.[cellIndex]?.fontWeight
                                                || (rowIndex === 0 ? 'bold' : 'normal'),
                                        }}
                                    >
                                        {cell}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', gap: '0.5rem' }}>
                {sheetNames.map((name) => (
                    <button
                        key={name}
                        onClick={() => setSelectedSheet(name)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: name === activeSheet ? '#009879' : '#f0f0f0',
                            color: name === activeSheet ? 'white' : '#333',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: name === activeSheet ? 'bold' : 'normal'
                        }}
                    >
                        {name}
                    </button>
                ))}
            </div>
        </div>
    );
};

const renderEML = (data, t) => {
    if (!data) return null;
    const email = data;

    const headerStyle = { paddingTop: '6px', paddingBottom: '4px', borderBottom: '1px solid rgb(236,236,236)', display: 'flex', gap: '8px' };
    const labelStyle = { fontWeight: 'bold', minWidth: '104px', color: '#555' };

    return (
        <div style={{ width: '100%', minHeight: '80vh', maxHeight: '80vh', overflowY: 'auto', textAlign: 'left' }}>
            <div style={{ backgroundColor: '#f5f5f5', padding: '6px 16px', borderRadius: '4px', marginBottom: '12px', fontSize: '14px' }}>
                {email.subject && <div style={headerStyle}><span style={labelStyle}>Objet :</span><span>{email.subject}</span></div>}
                {email.from && <div style={headerStyle}><span style={labelStyle}>De :</span><span>{email.from.name ? `${email.from.name} <${email.from.address}>` : email.from.address}</span></div>}
                {email.to?.length > 0 && <div style={headerStyle}><span style={labelStyle}>À :</span><span>{email.to.map(r => r.name ? `${r.name} <${r.address}>` : r.address).join(', ')}</span></div>}
                {email.cc?.length > 0 && <div style={headerStyle}><span style={labelStyle}>CC :</span><span>{email.cc.map(r => r.name ? `${r.name} <${r.address}>` : r.address).join(', ')}</span></div>}
                {email.date &&
                    <div style={headerStyle}>
                        <span style={labelStyle}>Date :</span>
                        <span>
                            {
                                (() => {
                                    try {
                                        return _.upperFirst(new Date(email.date).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
                                    } catch(e) { return email.date; }
                                })()
                            }</span></div>}
                {email.attachments && email.attachments.length > 0 && (
                    <div style={{ ...headerStyle, flexWrap: 'wrap', alignItems: 'center', borderBottom: 'none' }}>
                        <span style={labelStyle}>Pièces jointes :</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                            {email.attachments.map((att, i) => {
                                return <AttachmentItem key={i} att={att} attachments={email.attachments} index={i} />;
                            })}
                        </div>
                    </div>
                )}
            </div>
            <div style={{ padding: '8px', borderRadius: '4px', backgroundColor: '#fff', border: '1px solid #eee' }}>
                {email.html
                    ? <iframe srcDoc={email.html} style={{ width: '100%', minHeight: '460px', border: 'none' }} title="email-body" sandbox="allow-same-origin" />
                    : <div style={{ textAlign: 'center', padding: '1rem', fontSize: '20px' }}>
                        {t('FILE_GENERAL_ERROR')}
                    </div>
                }
            </div>
        </div>
    );
};

const renderPreview = ({ fileType, previewUrl, previewData, errorReadingFile, selectedSheet, setSelectedSheet, filename, t }) => {
    if (!errorReadingFile){
        switch (fileType) {
            case 'image':
                return <ImageViewer src={previewUrl} filename={filename} />;
            case 'video':
                return (
                    <video
                        src={previewUrl}
                        //poster={`/image/${dashboardData[element._id] && dashboardData[element._id].poster}`}
                        style={{outline: 'none', borderRadius: '4px'}}
                        width='100%'
                        controls
                        controlsList='nodownload'
                    />
                );
            case 'pdf':
                return (
                    <iframe
                        src={previewUrl}
                        style={{ width: '100%', height: '80vh' }}
                        title={filename}
                    />
                );
            case 'text':
                return (
                    <pre style={{
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto',
                        padding: '1rem',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        maxHeight: '80vh',
                        margin: 0,
                        textAlign: 'left'
                    }}>
                    {previewData}
                </pre>
                );
            case 'csv':
                return renderCSVTable(previewData);
            case 'xlsx':
                return renderXLSXTable(previewData, selectedSheet, setSelectedSheet);
            case 'eml':
                return renderEML(previewData, t);
            case 'docx':
                return (
                    <div
                        className="docx-container"
                        style={{
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            backgroundColor: '#f9f9f9',
                            padding: '1rem',
                            borderRadius: '4px',
                            textAlign: 'left',
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewData) }}
                    />
                );

            default:
                return (
                    <div style={{ textAlign: 'center', padding: '1rem', fontSize: '20px' }}>
                        {t('FILE_NOT_HANDLED_ERROR')}
                    </div>
                );
        }
    }
    else{
        return (
            <div style={{ textAlign: 'center', padding: '1rem', fontSize: '20px'}}>
                {t(errorReadingFile.message)}
            </div>
        );
    }
};

const PreviewModal = ({ blob, filename, onClose, attachments, initialIndex }) => {
    const [index, setIndex] = useState(initialIndex ?? null);
    const isGallery = attachments && attachments.length > 1;
    const currentBlob = isGallery ? (() => { const a = attachments[index]; return new Blob([a.content], { type: a.mimeType }); })() : blob;
    const currentFilename = isGallery ? attachments[index].filename : filename;

    const [isLoading, setIsLoading] = useState(true);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [errorReadingFile, setErrorReadingFile] = useState(null);
    const [selectedSheet, setSelectedSheet] = useState(null);
    const fileType = getFileType(currentFilename);

    React.useEffect(() => {
        setIsLoading(true);
        setPreviewUrl(null);
        setPreviewData(null);
        setErrorReadingFile(null);
        setSelectedSheet(null);
        processBlob(currentBlob, getFileType(currentFilename)).then(result => {
            if (result.error) setErrorReadingFile(result.error);
            else {
                setPreviewUrl(result.previewUrl || null);
                setPreviewData(result.previewData || null);
            }
            setIsLoading(false);
        });
        return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    }, [index]);

    const navButtonStyle = (hidden) => ({
        visibility: hidden ? 'hidden' : 'visible',
        background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
        width: '50px', height: '50px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        flexShrink: 0
    });

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001 }}>
            {isGallery && <button style={navButtonStyle(index === 0)} onClick={() => setIndex(i => i - 1)}><ChevronLeft size={22} /></button>}
            <div style={{ width: '80%', backgroundColor: 'white', padding: '10px 20px', borderRadius: '8px', maxWidth: '90%', maxHeight: '90%', overflow: 'auto', position: 'relative', margin: isGallery ? '0 8px' : 0 }}>
                <div style={{ position: 'absolute', right: '10px', top: '10px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {/*<a href={URL.createObjectURL(currentBlob)} download={currentFilename} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'inherit', display: 'flex' }}>
                        <Download size={20} />
                    </a>*/}
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                        <X size={20} />
                    </button>
                </div>
                <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
                    {currentFilename}
                </h3>
                <I18n ns={['platform']}>
                    {t => isLoading
                        ? <div style={{ textAlign: 'center', padding: '2rem' }}>{t('WAIT_LOADING')}</div>
                        : <div style={{ textAlign: 'center', maxHeight: '100%' }}>
                            {renderPreview({ fileType, previewUrl, previewData, errorReadingFile, selectedSheet, setSelectedSheet, filename: currentFilename, t })}
                          </div>
                    }
                </I18n>
            </div>
            {isGallery && <button style={navButtonStyle(index === attachments.length - 1)} onClick={() => setIndex(i => i + 1)}><ChevronRight size={22} /></button>}
        </div>,
        document.body
    );
};

const AttachmentItem = ({ att, attachments, index }) => {
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [blob, setBlob] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const canPreview = getFileType(att.filename) !== 'unknown';

    React.useEffect(() => {
        const b = new Blob([att.content], { type: att.mimeType });
        const blobUrl = URL.createObjectURL(b);
        setDownloadUrl(blobUrl);
        setBlob(b);
        return () => URL.revokeObjectURL(blobUrl);
    }, [att]);

    if (!downloadUrl) return null;

    return (
        <div style={{ border: '1px solid #ddd', borderRadius: '20px', padding: '4px 8px', maxWidth: '250px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {canPreview && (
                    <Eye size={14} style={{ cursor: 'pointer', color: 'rgba(33, 33, 33, 0.71)', flexShrink: 0 }}
                         onClick={(e) => { e.stopPropagation(); setShowPreview(true); }} />
                )}
                {/*<a href={downloadUrl} download={att.filename} title={att.filename} style={{ color: 'rgba(33, 33, 33, 0.71)', flexShrink: 0 }}>
                    <Download size={14} />
                </a>*/}
                <span title={att.filename} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }}>
                    {att.filename}
                </span>
            </div>
            {showPreview && blob && (
                <PreviewModal blob={blob} filename={att.filename} onClose={() => setShowPreview(false)} attachments={attachments} initialIndex={index} />
            )}
        </div>
    );
};

const FilePreviewAndDownload = ({url='file', file, files=[], language, groupModel, accessId, forceDisplayPreviewButton=false, noDownload=false, showDownloadMenu=false}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [downloadMenuPreview, setDownloadMenuPreview] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewData, setPreviewData] = useState(null); // New state for text content
    const [errorReadingFile, setErrorReadingFile] = useState(null); // New state for text content

    const [selectedSheet, setSelectedSheet] = useState(null); // maybe separate it later into another component (xslx previewer)

    const [selectedFile, setSelectedFile] = useState(file);

    React.useEffect(() => {
        if (!showPreview) {
            setSelectedFile(file);
        }
    }, [file]);

    const forceDisplayPreviewButtonIfAtLeastOnePreviewableElement = forceDisplayPreviewButton && files.some(obj => !obj.password && getFileType(obj.filename || obj.name) !== 'unknown')

    const { fileId, filename, password, fileType } = useMemo(() => {
        return {
            fileId: selectedFile._id || selectedFile.id,
            filename: selectedFile.filename || selectedFile.name,
            password: selectedFile.password,
            fileType: getFileType(selectedFile.filename || selectedFile.name)
        };
    }, [selectedFile]);

    const filesToPreview = files//.filter(obj => getFileType(obj.filename || obj.name) !== 'unknown' && !obj.password);

    const handleDownload = (t) => {
        toastr.info(t('downloading'))
        getFile({url, fileId, filename, password, language, groupModel, accessId})
    }

    const handlePreview = async (fileObj) => {
        setShowPreview(true);
        try {
            setIsLoading(true);

            const response = await fetch(
                `${window.location.origin}/${url}Download`,
                {
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Credentials": true
                    },
                    method: 'POST',
                    credentials: 'same-origin',
                    body: JSON.stringify({context: {id: fileObj.id, password: fileObj.password, language, groupModelId: groupModel && groupModel.id, accessId}})
                }
            );

            const blob = await response.blob();
            const fileType = getFileType(fileObj.filename || fileObj.name);

            const headerBytes = await blob.slice(0, 1024).text();
            if (headerBytes.includes('Something went wrong')) {
                setErrorReadingFile({
                    type: 'corruptedFileError',
                    message: 'FILE_CORRUPT_ERROR',
                });
                return;
            }

            //For video/image/pdf/other: limit preview to 100MB
            if (blob.size >= 100 * 1024 * 1024) {
                //toastr.warning();
                setErrorReadingFile({
                    type: 'bigFileError',
                    message: 'FILE_TOO_LARGE_ERROR',
                });
                return;
            }

            const result = await processBlob(blob, fileType);
            if (result.error) {
                setErrorReadingFile(result.error);
            } else {
                setPreviewUrl(result.previewUrl || null);
                setPreviewData(result.previewData || null);
            }
        } catch (error) {
            setErrorReadingFile({
                type: 'generalError',
                message: 'FILE_GENERAL_ERROR',
            });
            console.error('Error previewing file:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const navigateFiles = (direction) => {
        let result = null

        //Find next file
        const currentIndex = filesToPreview.findIndex(file => file.id === selectedFile.id);

        //Handle next file
        if (direction === 'next' && currentIndex < filesToPreview.length - 1) {
            return filesToPreview[currentIndex + 1]
        }
        if (direction === 'previous' && currentIndex > 0) {
            return filesToPreview[currentIndex - 1]// Returns previous file or null
        }
        return null // Returns next file or null
    };

    const handleNextFilePreview = (direction) => {
        //RESET
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setPreviewData(null); // Reset text content on close
        setSelectedSheet(null)
        setErrorReadingFile(null)

        //Get next file
        const nextFile = navigateFiles(direction)

        if (nextFile) {
            setSelectedFile(nextFile);
            handlePreview(nextFile); // ← this solves the problem
        }
    }

    const closePreview = () => {
        setShowPreview(false);
        setDownloadMenuPreview(false)
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setPreviewData(null); // Reset text content on close
        setSelectedSheet(null)
        setErrorReadingFile(null)
        setSelectedFile(file)
    };

    const renderPreviewContent = (t) => renderPreview({ fileType, previewUrl, previewData, errorReadingFile, selectedSheet, setSelectedSheet, filename, t });

    return (
        <I18n ns={[groupModel?.model?.id, 'platform']}>
            {t =>
                <>
                    <div className="filePeviewAndDownload-buttons" style={{
                        gap: '6px',
                        color: 'rgba(33, 33, 33, 0.71)',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}>
                        {(forceDisplayPreviewButtonIfAtLeastOnePreviewableElement || (!password && fileType !== 'unknown')) &&
                            <Eye className='Button'
                                size={16}
                                style={{ margin: 0, flexGrow: 0, flexShrink: 0 }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handlePreview(selectedFile);
                                }}
                        />}
                        {!noDownload &&
                            <Download className='Button' size={16}
                                 style={{ margin: 0, flexGrow: 0, flexShrink: 0 }}
                                 onClick={files.length > 1 && showDownloadMenu ?
                                     (event) => {
                                         event.stopPropagation();
                                         setShowPreview(true);
                                         setDownloadMenuPreview(true)
                                         renderPreviewContent(t)
                                     } : (event) => {
                                         event.stopPropagation();
                                         handleDownload(t)
                                     }
                                 }
                        />}
                    </div>

                    {showPreview && ReactDOM.createPortal(
                        <div className="preview-overlay"
                             style={{
                                 position: 'fixed',
                                 top: 0,
                                 left: 0,
                                 right: 0,
                                 bottom: 0,
                                 backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                 display: 'flex',
                                 justifyContent: 'center',
                                 alignItems: 'center',
                                 zIndex: 1000,
                                 maxHeight: '100%',
                             }}
                        >
                            {/* Previous Button (Left of container) */}
                            <button
                                style={{
                                    visibility: navigateFiles('previous') ? 'visible' : 'hidden',
                                    background: 'rgba(255,255,255,0.9)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '50px',
                                    height: '50px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    zIndex: 1001,
                                    marginRight: '20px', // Space between button and container
                                    flexShrink: 0
                                }}
                                onClick={() => handleNextFilePreview('previous')}
                            >
                                <ChevronLeft size={22} />
                            </button>

                            <div className="preview-container"
                                 style={{
                                     width: '80%',
                                     backgroundColor: 'white',
                                     padding: '10px 20px',
                                     borderRadius: '8px',
                                     maxWidth: '90%',
                                     maxHeight: '100%',
                                     overflow: 'auto',
                                     position: 'relative'
                                 }}
                            >
                                <button
                                    onClick={(event) => closePreview()}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '10px',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px'
                                    }}
                                >
                                    <X size={20}/>
                                </button>
                                <button
                                    onClick={closePreview}
                                    style={{
                                        position: 'absolute',
                                        right: '40px',
                                        top: '10px',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px'
                                    }}
                                >
                                    <Download size={18} onClick={(event) => {
                                        event.stopPropagation();
                                        handleDownload(t)
                                    }}/>
                                </button>
                                <h3 style={{
                                    marginBottom: '20px',
                                    //paddingRight: '10px',
                                    textAlign: 'center'
                                }}>
                                    {filename}
                                </h3>
                                {files.length > 0 && showDownloadMenu && downloadMenuPreview ? null :
                                    isLoading ? (
                                        <div style={{textAlign: 'center', padding: '2rem'}}>{t('WAIT_LOADING')}</div>
                                    ) : (
                                        <div style={{textAlign: 'center', maxHeight: '100%'}}>
                                            {renderPreviewContent(t)}
                                        </div>
                                    )}
                            </div>

                            {/* Next Button (Right of container) */}
                            <button
                                style={{
                                    visibility: navigateFiles('next') ? 'visible' : 'hidden',
                                    background: 'rgba(255,255,255,0.9)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '50px',
                                    height: '50px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    zIndex: 1001,
                                    marginLeft: '20px', // Space between button and container
                                    flexShrink: 0
                                }}
                                onClick={() => handleNextFilePreview('next')}
                            >
                                <ChevronRight size={22} />
                            </button>
                        </div>,
                        document.body
                    )}
                    <style jsx>{`
                ${docxFilesStyle}
            `}</style>
                </>
            }
        </I18n>
    );
};

export default FilePreviewAndDownload;
